'use client'

import type { MouseEvent, ReactNode } from 'react'
import { Anchor, Card, Flex, Stack, Text } from '@mantine/core'
import { usePathname, useRouter } from 'next/navigation'
import { setInsuranceReturnPath } from '@/lib/redirectFromInsuranceFlow'

type CompactVisitType = 'video' | 'chat' | 'econsult'

interface CompactVisitOptionRowProps {
    optionType: CompactVisitType
    icon: ReactNode
    title: string
    price: string
    insuranceSubtext?: string
    isUpsellInsurance?: boolean
    onClick?: () => void
}

interface CompactRowLabelProps {
    optionType: CompactVisitType
    icon: ReactNode
    title: string
}

function CompactRowLabel({ optionType, icon, title }: CompactRowLabelProps) {
    return (
        <Flex align="center" gap={12} style={{ minWidth: 0 }}>
            <Flex
                align="center"
                justify="center"
                style={{ flexShrink: 0 }}
                data-testid={`compact-visit-option-icon-${optionType}`}
            >
                {icon}
            </Flex>
            <Text
                c={'var(--Foreground-fg-primary, #1F1E1D)'}
                fz={16}
                fw={'var(--weight-text, 500)'}
                lh={'130%'}
                data-testid={`compact-visit-option-title-${optionType}`}
            >
                {title}
            </Text>
        </Flex>
    )
}

interface CompactRowPricingProps {
    optionType: CompactVisitType
    price: string
    insuranceSubtext?: string
    isUpsellInsurance: boolean
    onInsuranceLinkClick: (event: MouseEvent) => void
}

function CompactRowPricing({
    optionType,
    price,
    insuranceSubtext,
    isUpsellInsurance,
    onInsuranceLinkClick,
}: CompactRowPricingProps) {
    const showPrice = Boolean(price) && !isUpsellInsurance

    let primary: ReactNode = null
    if (isUpsellInsurance) {
        primary = (
            <Anchor
                component="button"
                type="button"
                fz={14}
                fw={'var(--weight-text-bold, 600)'}
                lh={'130%'}
                c="blue"
                onClick={onInsuranceLinkClick}
                data-testid={`compact-visit-option-insurance-link-${optionType}`}
            >
                Check insurance
            </Anchor>
        )
    } else if (showPrice) {
        primary = (
            <Text
                c={'var(--Foreground-fg-primary, #1F1E1D)'}
                fz={14}
                fw={'var(--weight-text-bold, 600)'}
                lh={'130%'}
                data-testid={`compact-visit-option-price-${optionType}`}
            >
                {price}
            </Text>
        )
    }

    if (!primary && !insuranceSubtext) {
        return null
    }

    return (
        <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
            {primary}
            {insuranceSubtext ? (
                <Text
                    ta="right"
                    c="var(--gm-foreground-fg-secondary)"
                    fz={12}
                    fw={'var(--weight-text, 450)'}
                    lh={'150%'}
                    data-testid={`compact-visit-option-insurance-${optionType}`}
                >
                    {insuranceSubtext}
                </Text>
            ) : null}
        </Stack>
    )
}

/**
 * Compact visit option row under "Other ways to start" when in-person is the
 * primary CTA. Icon and title sit on the left; price and insurance qualifier
 * subtext stack on the right. Signed-in users without insurance see an upsell
 * link instead of the headline price.
 */
export function CompactVisitOptionRow({
    optionType,
    icon,
    title,
    price,
    insuranceSubtext,
    isUpsellInsurance = false,
    onClick,
}: CompactVisitOptionRowProps) {
    const pathname = usePathname()
    const router = useRouter()

    const handleInsuranceLinkClick = (event: MouseEvent) => {
        event.stopPropagation()
        setInsuranceReturnPath(pathname)
        router.push('/insurance')
    }

    return (
        <Card
            withBorder
            radius="var(--border-16, 16px)"
            bg="var(--gm-background-bg-card)"
            bd="1px solid var(--Foreground-fg-outline-primary, #D1D0CF)"
            p="12px 16px"
            onClick={onClick}
            style={onClick ? { cursor: 'pointer' } : undefined}
            data-testid={`compact-visit-option-row-${optionType}`}
        >
            <Flex align="center" justify="space-between" gap={12} w="100%">
                <CompactRowLabel optionType={optionType} icon={icon} title={title} />
                <CompactRowPricing
                    optionType={optionType}
                    price={price}
                    insuranceSubtext={insuranceSubtext}
                    isUpsellInsurance={isUpsellInsurance}
                    onInsuranceLinkClick={handleInsuranceLinkClick}
                />
            </Flex>
        </Card>
    )
}
